import type { World } from '../world/world';
import { isSolidTile, ROOFS, BUILDING_WALLS, T } from '../world/tiles';
import { OUTLINE, ellipse, pill, rect, type Ctx } from './paint';
import { NPCS } from '../world/npcs';
import { npcName } from '../i18n/labels';
import { setWeatherLight } from './lighting';

/**
 * Weather overlay, drawn by the renderer right after the particles (before the
 * lighting pass, so night and overcast tint the rain too):
 * - slanted rain streaks in screen space (two depth layers, one stroke each),
 * - ground ripples at random visible spots (pooled), bigger rings on water,
 * - umbrellas over everyone walking in the rain,
 * - typhoon debris flying past, lightning flashes (added by the lighting pass),
 * - indoors: rain running down the windows only.
 * Plus the screen-shake offset used for earthquakes.
 */

export interface SkyState {
  /** Rain strength 0..1 (0 when dry). */
  rain: number;
  /** Wind 0..1 (1 = typhoon). */
  wind: number;
  /** Cloud cover 0..1 for the lighting. */
  gloom: number;
  /** Lightning on (storm / typhoon while it pours). */
  thunder: boolean;
  /** Does the player carry an umbrella? */
  umbrella: boolean;
}

let source: () => SkyState = () => ({ rain: 0, wind: 0, gloom: 0, thunder: false, umbrella: false });
const frameHooks: ((world: World, dt: number) => void)[] = [];

/** The sky package tells the overlay what the weather is. */
export function setSkySource(fn: () => SkyState): void {
  source = fn;
}
/** Per-frame callbacks (the sky package's logic: ambience, events). Run before drawing. */
export function onSkyFrame(fn: (world: World, dt: number) => void): void {
  frameHooks.push(fn);
}

// --- Screen shake ---------------------------------------------------------------
let shake = { t: 0, dur: 0, amp: 0 };
export function startShake(seconds = 3, amp = 3): void {
  shake = { t: 0, dur: seconds, amp };
}
export function shaking(): boolean {
  return shake.t < shake.dur;
}
/** Camera offset (logical px) for this frame. */
export function shakeOffset(): { x: number; y: number } {
  if (shake.t >= shake.dur) return { x: 0, y: 0 };
  const k = shake.t / shake.dur;
  // Quick build-up, rolling middle, long fade.
  const env = Math.min(1, k / 0.12) * (k < 0.55 ? 1 : 1 - (k - 0.55) / 0.45) * shake.amp;
  const t = shake.t;
  return {
    x: env * (Math.sin(t * 37) * 0.6 + Math.sin(t * 23 + 1) * 0.4),
    y: env * 0.6 * (Math.sin(t * 29 + 2) * 0.6 + Math.sin(t * 41) * 0.4),
  };
}

// --- Rain streaks (screen space) ----------------------------------------------
interface Streak {
  x: number;
  y: number;
  v: number;
  len: number;
  near: boolean;
}
const streaks: Streak[] = [];
let lastCam = { x: 0, y: 0, map: '' };

function newStreak(w: number, h: number, top: boolean): Streak {
  const near = Math.random() < 0.35;
  return { x: Math.random() * (w + 80) - 40, y: top ? -Math.random() * 40 : Math.random() * h, v: near ? 430 + Math.random() * 120 : 300 + Math.random() * 90, len: near ? 11 + Math.random() * 6 : 6 + Math.random() * 3, near };
}

// --- Ripples (world space, pooled) --------------------------------------------
interface Ripple {
  x: number;
  y: number;
  age: number;
  life: number;
  r: number;
  water: boolean;
  on: boolean;
}
const RIPPLES = 56;
const ripples: Ripple[] = Array.from({ length: RIPPLES }, () => ({ x: 0, y: 0, age: 0, life: 1, r: 3, water: false, on: false }));
let rippleAcc = 0;

function spawnRipple(world: World, vx: number, vy: number, vw: number, vh: number): void {
  const slot = ripples.find((r) => !r.on);
  if (!slot) return;
  // A few tries to land on open ground (not roofs or walls).
  for (let tries = 0; tries < 4; tries++) {
    const x = vx + Math.random() * vw, y = vy + 20 + Math.random() * (vh - 20);
    const ch = world.map.rows[Math.floor(y / T)]?.[Math.floor(x / T)];
    if (!ch || ch === ' ' || ROOFS.has(ch) || BUILDING_WALLS.has(ch)) continue;
    const water = ch === '~' || ch === 'q' || ch === 'Z' || ch === 'P';
    if (!water && isSolidTile(ch)) continue;
    Object.assign(slot, { x, y, age: 0, life: water ? 0.9 + Math.random() * 0.4 : 0.45 + Math.random() * 0.25, r: water ? 4 + Math.random() * 3 : 2 + Math.random() * 1.6, water, on: true });
    return;
  }
}

// --- Typhoon debris (screen space) --------------------------------------------
interface Leaf {
  x: number;
  y: number;
  v: number;
  spin: number;
  color: string;
}
const leaves: Leaf[] = [];
const LEAF_COLORS = ['#5f9a3e', '#7fb04a', '#a4823c', '#4d7d33', '#c9b27a'];

// --- Lightning ------------------------------------------------------------------
let flash = 0;
let nextBolt = 6;
let thunderHook: (delay: number) => void = () => {};
/** Called with the delay (s) before the thunder should be heard. */
export function onThunder(fn: (delay: number) => void): void {
  thunderHook = fn;
}
export function lightning(): void {
  flash = 1;
  thunderHook(0.35 + Math.random() * 1.8);
}

// --- Umbrellas ------------------------------------------------------------------
const UMB = ['#e8504a', '#3f7fd8', '#f2c23a', '#5bb36a', '#b45bc9', '#f08fb0', '#33a3a8'];
function umbrella(c: Ctx, x: number, y: number, color: string, tilt: number): void {
  c.save();
  c.translate(x, y);
  c.rotate(tilt);
  rect(c, OUTLINE, -0.5, -22, 1, 15);
  // Canopy: a scalloped dome.
  c.fillStyle = OUTLINE;
  c.beginPath();
  c.ellipse(0, -22, 12.5, 7.5, 0, Math.PI, 0);
  c.fill();
  c.fillStyle = color;
  c.beginPath();
  c.ellipse(0, -22, 11.5, 6.6, 0, Math.PI, 0);
  c.fill();
  c.fillStyle = 'rgba(255,255,255,0.28)';
  c.beginPath();
  c.ellipse(-3.5, -24.5, 5, 3, -0.3, Math.PI, 0);
  c.fill();
  c.fillStyle = 'rgba(0,0,0,0.2)';
  for (const sx of [-6, 0, 6]) c.fillRect(sx - 0.3, -27 + Math.abs(sx) * 0.35, 0.6, 5);
  // Scallops on the rim.
  c.fillStyle = color;
  for (let i = -11; i <= 9; i += 5) {
    c.beginPath();
    c.arc(i + 2.5, -22, 2.6, 0, Math.PI);
    c.fill();
  }
  rect(c, OUTLINE, -0.5, -30, 1, 2);
  c.restore();
}

/** Cheap hash so each NPC keeps its own umbrella colour. */
function hashColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return UMB[Math.abs(h) % UMB.length];
}

/**
 * The renderer's weather hook. `cam` is the visible rectangle in world
 * coordinates; the context comes in with the world transform (scale Z).
 */
export function drawWeather(c: Ctx, world: World, cam: { x: number; y: number; w: number; h: number }, Z: number, dt: number): void {
  const t0 = performance.now();
  for (const f of frameHooks) f(world, dt);
  drawWeatherInner(c, world, cam, Z, dt);
  cost.ms += performance.now() - t0;
  cost.frames++;
}
const cost = { ms: 0, frames: 0 };

function drawWeatherInner(c: Ctx, world: World, cam: { x: number; y: number; w: number; h: number }, Z: number, dt: number): void {
  shake.t += dt;
  const sky = source();
  const indoor = world.map.indoor;
  flash = Math.max(0, flash - dt * 3.2);
  // Lightning: only while it pours.
  if (sky.thunder && sky.rain > 0.5) {
    nextBolt -= dt;
    if (nextBolt <= 0) {
      nextBolt = 7 + Math.random() * 14;
      lightning();
    }
  }
  // Double-pulse flicker.
  const fl = flash > 0.6 ? flash : flash > 0.35 ? flash * 0.3 : flash * 0.9;
  setWeatherLight(indoor ? sky.gloom * 0.6 : sky.gloom, indoor ? 0 : wetness(sky.rain, dt), indoor ? fl * 0.25 : fl * 0.55);

  if (indoor) {
    if (sky.rain > 0.02) drawWindowRain(c, world, sky, cam);
    lastCam.map = '';
    return;
  }
  const raining = sky.rain > 0.02;
  if (!raining && !leaves.length && !ripples.some((r) => r.on)) return;

  // Ripples and splashes on the ground (world space).
  if (raining) {
    rippleAcc += dt * (18 + 70 * sky.rain) * ((cam.w * cam.h) / (250 * 460));
    while (rippleAcc >= 1) {
      rippleAcc--;
      spawnRipple(world, cam.x, cam.y, cam.w, cam.h);
    }
  }
  c.lineWidth = 0.7;
  for (const r of ripples) {
    if (!r.on) continue;
    r.age += dt;
    const k = r.age / r.life;
    if (k >= 1) {
      r.on = false;
      continue;
    }
    const a = (1 - k) * (r.water ? 0.65 : 0.55);
    c.strokeStyle = r.water ? `rgba(225,240,255,${a})` : `rgba(210,225,240,${a})`;
    c.beginPath();
    c.ellipse(r.x, r.y, 0.6 + r.r * k, (0.6 + r.r * k) * 0.42, 0, 0, Math.PI * 2);
    c.stroke();
    if (r.water && k > 0.3) {
      c.beginPath();
      c.ellipse(r.x, r.y, r.r * (k - 0.3) * 0.8, r.r * (k - 0.3) * 0.34, 0, 0, Math.PI * 2);
      c.stroke();
    }
    // A tiny crown of droplets right at the impact.
    if (k < 0.22 && !r.water) {
      const up = Math.sin((k / 0.22) * Math.PI) * 2.2;
      c.fillStyle = `rgba(230,240,255,${0.7 * (1 - k)})`;
      c.fillRect(r.x - 1.6, r.y - up, 0.7, 0.7);
      c.fillRect(r.x + 1, r.y - up * 0.8, 0.7, 0.7);
    }
  }

  // Umbrellas: the player's (if they have one) and every NPC walking outside.
  if (raining) {
    const p = world.player;
    const tiltBase = -0.12 - sky.wind * 0.25;
    if (sky.umbrella && !world.inBus) umbrella(c, p.x, p.y - 8 - world.hopZ, '#3f7fd8', tiltBase + (p.moving ? Math.sin(world.time * 9) * 0.04 : 0));
    for (const [id, rt] of world.npcs) {
      const a = rt.actor;
      if (a.x < cam.x - 20 || a.x > cam.x + cam.w + 20 || a.y < cam.y - 10 || a.y > cam.y + cam.h + 40) continue;
      if (id === 'horse' || id === 'pony' || id === 'cat') continue;
      umbrella(c, a.x, a.y - 8, hashColor(id), tiltBase + Math.sin(world.time * 1.3 + a.x) * 0.03);
      // Keep the name tag readable on top of the umbrella (same rule as the renderer's tags).
      const def = NPCS.find((n) => n.id === id);
      const focused = world.near?.kind === 'npc' && world.near.npc.id === id;
      if (def?.look && !focused) {
        c.globalAlpha = 0.85;
        pill(c, npcName(def.label), a.x, a.y - 34, '#ffe08a');
        c.globalAlpha = 1;
      }
    }
  }

  // Screen-space layers from here on.
  c.save();
  c.setTransform(Z, 0, 0, Z, 0, 0);
  const vw = cam.w, vh = cam.h;
  // Keep the rain anchored to the world when the camera scrolls.
  const dx = lastCam.map === world.map.id ? cam.x - lastCam.x : 0;
  const dy = lastCam.map === world.map.id ? cam.y - lastCam.y : 0;
  lastCam = { x: cam.x, y: cam.y, map: world.map.id };

  const want = raining ? Math.min(460, Math.round((50 + 300 * sky.rain + 90 * sky.wind) * ((vw * vh) / (250 * 460)))) : 0;
  while (streaks.length < want) streaks.push(newStreak(vw, vh, false));
  if (streaks.length > want) streaks.length = want;
  const slant = 0.18 + sky.wind * 0.75;
  // Typhoon gusts sweep the rain in sheets.
  const gust = sky.wind > 0.6 ? 0.75 + 0.25 * Math.sin(world.time * 1.7) * Math.sin(world.time * 0.6 + 2) : 1;
  for (const layer of [false, true]) {
    // Drawn before the lighting pass (which dims them), so they start bright.
    c.strokeStyle = layer ? `rgba(235,244,255,${Math.min(1, 0.7 + 0.3 * sky.rain) * gust})` : `rgba(205,220,245,${(0.5 + 0.2 * sky.rain) * gust})`;
    c.lineWidth = layer ? 1 : 0.7;
    c.beginPath();
    for (const s of streaks) {
      if (s.near !== layer) continue;
      s.y += s.v * dt - dy;
      s.x += -s.v * slant * dt - dx;
      if (s.y > vh + 10 || s.x < -50 || s.x > vw + vh * slant + 60 || s.y < -60) Object.assign(s, newStreak(vw, vh, true), { x: Math.random() * (vw + vh * slant * 0.8) });
      c.moveTo(s.x, s.y);
      c.lineTo(s.x + s.len * slant, s.y - s.len);
    }
    c.stroke();
  }

  // Typhoon: leaves and bits of paper ripping across the screen.
  const wantLeaves = sky.wind > 0.6 ? 14 : 0;
  if (leaves.length < wantLeaves && Math.random() < dt * 6) leaves.push({ x: vw + 10, y: Math.random() * vh, v: 140 + Math.random() * 160, spin: Math.random() * 6, color: LEAF_COLORS[Math.floor(Math.random() * LEAF_COLORS.length)] });
  for (let i = leaves.length - 1; i >= 0; i--) {
    const l = leaves[i];
    l.x -= l.v * dt + dx;
    l.y += Math.sin(world.time * 3 + l.spin) * 30 * dt - dy;
    l.spin += dt * 9;
    if (l.x < -20) {
      leaves.splice(i, 1);
      continue;
    }
    c.save();
    c.translate(l.x, l.y);
    c.rotate(l.spin);
    c.scale(1, Math.abs(Math.cos(l.spin * 0.7)) * 0.8 + 0.2);
    ellipse(c, l.color, 0, 0, 2.6, 1.3);
    c.restore();
  }

  // Grey sheet of distant rain in a typhoon.
  if (sky.rain > 0.6 && sky.wind > 0.6) {
    c.fillStyle = `rgba(160,175,195,${0.1 * gust * sky.rain})`;
    c.fillRect(0, 0, vw, vh);
  }
  c.restore();
}

/** Ground wetness 0..1: soaks up quickly in the rain, dries slowly afterwards. */
let wet = 0;
function wetness(rain: number, dt: number): number {
  if (rain > 0.02) wet = Math.min(1, wet + dt * (0.15 + rain * 0.5));
  else wet = Math.max(0, wet - dt * 0.012);
  return wet;
}
/** For tests and the ambience (wet footsteps). */
export function groundWet(): number {
  return wet;
}

/** Indoors: rain trickling down the window panes (and the flash of lightning through them). */
function drawWindowRain(c: Ctx, world: World, sky: SkyState, cam: { x: number; y: number; w: number; h: number }): void {
  const t = world.time;
  for (const d of world.map.decor) {
    if (d.kind !== 'window' || d.y > 4) continue;
    const w = d.w ?? 32;
    const x0 = d.x + 2.5, y0 = d.y + 3.5, gw = w - 5, gh = 9;
    if (x0 + gw < cam.x || x0 > cam.x + cam.w || y0 + gh < cam.y || y0 > cam.y + cam.h) continue;
    c.save();
    c.beginPath();
    c.rect(x0, y0, gw, gh);
    c.clip();
    c.fillStyle = `rgba(70,88,120,${0.35 + sky.rain * 0.3})`;
    c.fillRect(x0, y0, gw, gh);
    c.strokeStyle = 'rgba(210,225,245,0.55)';
    c.lineWidth = 0.4;
    c.beginPath();
    const n = Math.round(gw / 3);
    for (let i = 0; i < n; i++) {
      const sx = x0 + ((i * 7.3 + Math.floor(t * 2 + i) * 3.1) % gw);
      const sy = y0 + ((t * (14 + (i % 3) * 5) + i * 4.7) % (gh + 4)) - 3;
      c.moveTo(sx, sy);
      c.lineTo(sx - 0.6 - sky.wind, sy + 2.5);
    }
    c.stroke();
    // Drops clinging to the glass.
    c.fillStyle = 'rgba(230,240,255,0.6)';
    for (let i = 0; i < n; i++) c.fillRect(x0 + ((i * 11.7) % gw), y0 + ((i * 5.3 + Math.floor(t / 3 + i)) % gh), 0.6, 0.8);
    c.restore();
  }
}

/** Debug snapshot (window.__debug.sky.state()). */
export function weatherDebug(reset = false): { sky: SkyState; streaks: number; ripples: number; wet: number; flash: number; msPerFrame: number } {
  const out = { sky: source(), streaks: streaks.length, ripples: ripples.filter((r) => r.on).length, wet, flash, msPerFrame: cost.frames ? cost.ms / cost.frames : 0 };
  if (reset) cost.ms = cost.frames = 0;
  return out;
}
